import React, { useEffect, useMemo, useState } from 'react';
import SKAlerts, { CreateAlert } from "../../shared/common-alerts/SKAlerts";
import VehiclePurchaseRegisterGrid from './VehiclePurchaseRegisterGrid';
import DealerDropdown from '../../shared/common-dropdown/DealerDropdown';
import VehicleDropdown from '../../shared/common-dropdown/VehicleDropdown';
import { logAuditEvent } from '../../utils/auditLogger';

const getTodayDate = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const toInputDate = (value) => {
  if (!value) return getTodayDate();
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return getTodayDate();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

function AddPurchaseRegister({ initialValues = {}, onSubmit, moduleName = 'Purchase Register', formId = 'purchaseRegisterForm' }) {
  const [alert, setAlert] = useState(null);
  const [DealerId, setDealerId] = useState(initialValues.DealerId || '');
  const [DealerName, setDealerName] = useState(initialValues.DealerName || '');
  const [PurchaseDate, setPurchaseDate] = useState(toInputDate(initialValues.PurchaseDate));
  const [Remarks, setRemarks] = useState(initialValues.Remarks || '');
  const [VehicleId, setVehicleId] = useState('');
  const [VehicleLabel, setVehicleLabel] = useState('');
  const [selectedVehicleOption, setSelectedVehicleOption] = useState(null);
  const [VehicleCost, setVehicleCost] = useState('');
  const [Quantity, setQuantity] = useState(1);
  const [ChassisNo, setChassisNo] = useState('');
  const [EngineNo, setEngineNo] = useState('');
  const [details, setDetails] = useState(initialValues.PurchaseRegisterDetails || []);
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    logAuditEvent(moduleName, 'Accessed Module', `Accessed ${moduleName}`);
  }, [moduleName]);

  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      setDealerId(initialValues.DealerId || '');
      setDealerName(initialValues.DealerName || '');
      setPurchaseDate(toInputDate(initialValues.PurchaseDate));
      setRemarks(initialValues.Remarks || '');
      setDetails(initialValues.PurchaseRegisterDetails || []);
      // Force dropdowns to re-sync when new data loads
      setResetKey(prev => prev + 1);
    }
  }, [initialValues]);

  const addDetail = () => {
    if (!VehicleId || !Quantity) {
      CreateAlert('Vehicle and Quantity are required', 'Warning', setAlert);
      return;
    }
    const qty = Number(Quantity);
    const newEntries = [];
    for (let i = 0; i < qty; i++) {
      const entry = {
        VehicleId: Number(VehicleId),
        VehicleLabel: VehicleLabel || (selectedVehicleOption?.label ?? ''),
        VehicleCost: VehicleCost ? Number(VehicleCost) : null,
        Quantity: 1,
        ChassisNo: ChassisNo || '',
        EngineNo: EngineNo || '',
        Selected: true
      };
      newEntries.push(entry);
    }
    setDetails((prev) => [...prev, ...newEntries]);
    try {
      logAuditEvent(moduleName, 'Add Button Click', JSON.stringify({ VehicleId: Number(VehicleId), Quantity: qty }));
    } catch (e) {}
    setVehicleId('');
    setSelectedVehicleOption(null);
    setVehicleCost('');
    setQuantity(1);
    setChassisNo('');
    setEngineNo('');
  };

  const removeDetail = (index) => {
    setDetails((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!DealerId) {
      CreateAlert('Dealer Name is required!!', 'Warning', setAlert);
      return;
    }
    if (details.length === 0) {
      CreateAlert('At least one vehicle must be added!!', 'Warning', setAlert);
      return;
    }
    const missingChassisOrEngine = details.some(d => !d.ChassisNo?.trim() || !d.EngineNo?.trim());
    if (missingChassisOrEngine) {
      CreateAlert('Chassis Number and Engine Number are mandatory for all vehicles!!', 'Warning', setAlert);
      return;
    }
    const payload = {
      DealerId,
      PurchaseDate,
      Remarks,
      PurchaseRegisterDetails: details.filter(d => d.Selected)
    };
    onSubmit && onSubmit(e, payload);
  };

  const maxDate = useMemo(() => getTodayDate(), []);

  return (
    <>
      <SKAlerts alert={alert} />
      <form id={formId} onSubmit={handleSubmit}>
        <div className="row g-3">
          <div className="col-md-8">
            <DealerDropdown
              key={`dealer-${resetKey}`}
              selectedValue={DealerId ? String(DealerId) : ''}
              required
              onChangeDropdown={(option) => {
                setDealerId(option?.value || '');
                setDealerName(option?.label || '');
              }}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Purchase Date</label>
            <input type="date" className="form-control" value={PurchaseDate} max={maxDate} onChange={(e) => setPurchaseDate(e.target.value)} />
          </div>
        </div>

        <hr />
        <div className="row g-3">
          <div className="col-md-8">
            <VehicleDropdown
              required
              selectedValue={VehicleId ? String(VehicleId) : ''}
              onChangeDropdown={(option) => {
                setSelectedVehicleOption(option || null);
                setVehicleId(option?.value || '');
                setVehicleLabel(option?.label || '');
              }}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Cost</label>
            <input type="text" className="form-control" value={VehicleCost} onChange={(e) => setVehicleCost(e.target.value)} />
          </div>
          <div className="col-md-2">
            <label className="form-label">Quantity *</label>
            <input type="number" min="1" className="form-control" value={Quantity} onChange={(e) => setQuantity(e.target.value)} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Chassis No</label>
            <input className="form-control" value={ChassisNo} onChange={(e) => setChassisNo(e.target.value)} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Engine No</label>
            <input className="form-control" value={EngineNo} onChange={(e) => setEngineNo(e.target.value)} />
          </div>
          <div className="col-12 d-flex justify-content-end gap-2">
            <button 
              type="button" 
              className="btn btn-cancel" 
              onClick={() => {
                setVehicleId('');
                setSelectedVehicleOption(null);
                setVehicleCost('');
                setQuantity(1);
                setChassisNo('');
                setEngineNo('');
              }}
            >
              Reset
            </button>
            <button type="button" className="btn btn-submit" onClick={addDetail}>
              Add Vehicle
            </button>
          </div>
        </div>

        <div className="mt-3">
          <VehiclePurchaseRegisterGrid
            details={details}
            onRemove={removeDetail}
            onUpdate={(index, field, value) => {
              setDetails(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
            }}
            moduleName={moduleName}
          />
        </div>
        <div className="col-md-12">
          <label className="form-label">Remarks</label>
          <input className="form-control" value={Remarks} onChange={(e) => setRemarks(e.target.value)} />
        </div>
      </form>
    </>
  );
}

export default AddPurchaseRegister;
